/**
 * Schlagwörter aus Open Food Facts (zum Beispiel „en:pastas“, „en:gluten“,
 * „en:organic“) für die Oberfläche: Warengruppe der Einkaufsliste, Allergene
 * und Siegel auf Deutsch.
 */
import type { ShoppingCategory } from './shopping.ts';

/**
 * Kategorie-Schlagwörter und ihre Warengruppe. Genauere Einträge stehen vorn
 * („en:sweet-spreads“ vor „en:spreads“, „en:frozen-foods“ vor allem anderen,
 * weil Tiefkühlware im Laden in der Truhe liegt).
 */
const CATEGORY_TAGS: readonly (readonly [string, ShoppingCategory])[] = [
  ['en:frozen-foods', 'frozen'],
  ['en:ice-creams', 'frozen'],
  ['en:beverages', 'drinks'],
  ['en:waters', 'drinks'],
  ['en:sweet-spreads', 'sweets'],
  ['en:chocolates', 'sweets'],
  ['en:confectioneries', 'sweets'],
  ['en:candies', 'sweets'],
  ['en:biscuits-and-cakes', 'sweets'],
  ['en:biscuits', 'sweets'],
  ['en:sweet-snacks', 'sweets'],
  ['en:salty-snacks', 'sweets'],
  ['en:snacks', 'sweets'],
  ['en:cheeses', 'dairy'],
  ['en:yogurts', 'dairy'],
  ['en:milks', 'dairy'],
  ['en:creams', 'dairy'],
  ['en:butters', 'dairy'],
  ['en:eggs', 'dairy'],
  ['en:dairies', 'dairy'],
  ['en:fishes', 'meat'],
  ['en:seafood', 'meat'],
  ['en:sausages', 'meat'],
  ['en:hams', 'meat'],
  ['en:meats', 'meat'],
  ['en:breads', 'bakery'],
  ['en:pastries', 'bakery'],
  ['en:viennoiseries', 'bakery'],
  ['en:flours', 'baking'],
  ['en:sugars', 'baking'],
  ['en:baking-decorations', 'baking'],
  ['en:leavening-agents', 'baking'],
  ['en:baking-mixes', 'baking'],
  ['en:spices', 'spices'],
  ['en:herbs', 'spices'],
  ['en:salts', 'spices'],
  ['en:condiments', 'spices'],
  ['en:sauces', 'spices'],
  ['en:vegetable-oils', 'spices'],
  ['en:vinegars', 'spices'],
  ['en:pastas', 'pantry'],
  ['en:rices', 'pantry'],
  ['en:legumes', 'pantry'],
  ['en:canned-foods', 'pantry'],
  ['en:breakfast-cereals', 'pantry'],
  ['en:cereals-and-their-products', 'pantry'],
  ['en:soups', 'pantry'],
  ['en:fresh-vegetables', 'produce'],
  ['en:fresh-fruits', 'produce'],
  ['en:vegetables', 'produce'],
  ['en:fruits', 'produce'],
];

/** Warengruppe aus den Kategorien eines Produkts oder null, wenn keine passt. */
export function categoryFromTags(tags: readonly string[]): ShoppingCategory | null {
  for (const [tag, category] of CATEGORY_TAGS) {
    if (tags.includes(tag)) {
      return category;
    }
  }
  return null;
}

/** Die 14 Hauptallergene der EU-Lebensmittelinformationsverordnung. */
const ALLERGENS: Readonly<Record<string, string>> = {
  'en:gluten': 'Gluten',
  'en:crustaceans': 'Krebstiere',
  'en:eggs': 'Eier',
  'en:fish': 'Fisch',
  'en:peanuts': 'Erdnüsse',
  'en:soybeans': 'Soja',
  'en:milk': 'Milch',
  'en:nuts': 'Schalenfrüchte',
  'en:celery': 'Sellerie',
  'en:mustard': 'Senf',
  'en:sesame-seeds': 'Sesam',
  'en:sulphur-dioxide-and-sulphites': 'Schwefeldioxid und Sulfite',
  'en:lupin': 'Lupinen',
  'en:molluscs': 'Weichtiere',
};

/** Siegel und Hinweise, die beim Einkaufen helfen. */
const LABELS: Readonly<Record<string, string>> = {
  'en:organic': 'Bio',
  'en:eu-organic': 'EU-Bio',
  'en:vegan': 'vegan',
  'en:vegetarian': 'vegetarisch',
  'en:fair-trade': 'Fairtrade',
  'en:fairtrade-international': 'Fairtrade',
  'en:no-gluten': 'glutenfrei',
  'en:gluten-free': 'glutenfrei',
  'en:no-lactose': 'laktosefrei',
  'en:lactose-free': 'laktosefrei',
  'en:palm-oil-free': 'ohne Palmöl',
  'en:no-added-sugar': 'ohne Zuckerzusatz',
  'en:msc': 'MSC',
  'en:rainforest-alliance': 'Rainforest Alliance',
};

/** Deutsche Namen der bekannten Allergene; Unbekanntes fällt weg. */
export function allergenNames(tags: readonly string[]): string[] {
  return [...new Set(tags.map((tag) => ALLERGENS[tag]).filter((name): name is string => name !== undefined))];
}

/** Deutsche Namen der bekannten Siegel; Unbekanntes fällt weg. */
export function labelNames(tags: readonly string[]): string[] {
  return [...new Set(tags.map((tag) => LABELS[tag]).filter((name): name is string => name !== undefined))];
}
